import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const BranchContext = createContext(null);

export function BranchProvider({ children }) {
  const { user } = useAuth();
  const [schools, setSchools] = useState([]);
  const [allBranches, setAllBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSchoolId, setSelectedSchoolIdRaw] = useState(
    () => localStorage.getItem("sb_selected_school") || null
  );
  const [selectedBranchId, setSelectedBranchIdRaw] = useState(
    () => localStorage.getItem("sb_selected_branch") || null
  );

  const setSelectedSchoolId = useCallback((id) => {
    setSelectedSchoolIdRaw(id);
    if (id) localStorage.setItem("sb_selected_school", id);
    else localStorage.removeItem("sb_selected_school");
  }, []);

  const setSelectedBranchId = useCallback((id) => {
    setSelectedBranchIdRaw(id);
    if (id) localStorage.setItem("sb_selected_branch", id);
    else localStorage.removeItem("sb_selected_branch");
  }, []);

  const refresh = useCallback(async () => {
    if (!user || !["school_owner", "branch_admin", "special_access"].includes(user.role)) {
      setSchools([]); setAllBranches([]); setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const [s, b] = await Promise.all([
        api.get("/schools/mine"),
        api.get("/branches/my"),
      ]);
      setSchools(s.data || []);
      setAllBranches(b.data || []);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => { refresh(); }, [refresh]);

  // Approved schools and approved branches under selected school
  const approvedSchools = useMemo(
    () => schools.filter((s) => s.status === "approved"),
    [schools]
  );

  const branchesForSelectedSchool = useMemo(
    () => allBranches.filter((b) => b.school_id === selectedSchoolId && b.status === "approved"),
    [allBranches, selectedSchoolId]
  );

  // Auto-correct stale selection when data arrives.
  // Preference: keep current valid selection > school that has approved branches > any approved school
  useEffect(() => {
    if (loading) return;
    if (approvedSchools.length === 0) {
      if (selectedSchoolId) setSelectedSchoolId(null);
      if (selectedBranchId) setSelectedBranchId(null);
      return;
    }
    const branchesUnder = (sid) =>
      allBranches.filter((b) => b.school_id === sid && b.status === "approved");

    const stillValidSchool = approvedSchools.find((s) => s.id === selectedSchoolId);
    // If current selection has zero approved branches but another school has them, prefer the productive one
    const needsBetterSchool =
      !stillValidSchool ||
      (branchesUnder(selectedSchoolId).length === 0 &&
        approvedSchools.some((s) => branchesUnder(s.id).length > 0));

    if (needsBetterSchool) {
      const productive = approvedSchools.find((s) => branchesUnder(s.id).length > 0);
      const target = productive || approvedSchools[0];
      if (target.id !== selectedSchoolId) {
        setSelectedSchoolId(target.id);
        setSelectedBranchId(null);
        return;
      }
    }
    const branches = branchesUnder(selectedSchoolId);
    if (branches.length === 0) {
      if (selectedBranchId) setSelectedBranchId(null);
      return;
    }
    const stillValidBranch = branches.find((b) => b.id === selectedBranchId);
    if (!stillValidBranch) setSelectedBranchId(branches[0].id);
  }, [loading, approvedSchools, allBranches, selectedSchoolId, selectedBranchId, setSelectedSchoolId, setSelectedBranchId]);

  const selectedSchool = approvedSchools.find((s) => s.id === selectedSchoolId) || null;
  const selectedBranch = branchesForSelectedSchool.find((b) => b.id === selectedBranchId) || null;

  const value = {
    schools,
    approvedSchools,
    allBranches,
    branchesForSelectedSchool,
    selectedSchool,
    selectedBranch,
    selectedSchoolId,
    selectedBranchId,
    setSelectedSchoolId: (id) => {
      setSelectedSchoolId(id);
      setSelectedBranchId(null); // reset branch when school changes
    },
    setSelectedBranchId,
    refresh,
    loading,
  };

  return <BranchContext.Provider value={value}>{children}</BranchContext.Provider>;
}

export function useBranch() {
  const ctx = useContext(BranchContext);
  if (!ctx) throw new Error("useBranch must be used within BranchProvider");
  return ctx;
}
